import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ChangeEvent, useEffect, useState, Dispatch } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useSession } from '../../hooks/session';
import {
  KEY_BOOK_GROUP_CATEGORY_IMAGE_LIST,
  KEY_BOOK_GROUP_CATEGORY_LIST,
} from '../../common/key';
import { encodeSearchWord } from '../../common/helper';
import {
  getBookGroupCategoryImageList,
  getBookGroupCategoryList,
} from '../../common/api/ApiBook';
import SwLoading from './spinner/SwLoading';
import { SwPagination } from './tableElement/SwPagination';

interface Props {
  setImageUrl: Dispatch<React.SetStateAction<string>>;
  width?: string;
  height?: string;
  open: boolean;
  onClose: () => void;
}

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

export const tabWidth = '5.5rem';

export const SwThumbImageCategoryView: React.FC<Props> = ({
  setImageUrl,
  width = '100rem',
  height = 'auto',
  open,
  onClose,
}) => {
  const { loginVal } = useSession();
  const queryClient = useQueryClient();
  const [selectedCategorySeq, setSelectedCategorySeq] = useState<string>('0');
  const [bookGroupCategoryList, setBookGroupCategoryList] = useState<any>([]);
  const [bookGroupCategoryImageList, setBookGroupCategoryImageList] =
    useState<any>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [reqRequest, setReqRequest] = useState<boolean>(false);
  const [searchKey, setSearchKey] = useState<string>('book-group-category');
  const [searchWord, setSearchWord] = useState<string>('');
  const [loading, setLoading] = useState<any>(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(9);
  const rowsPerPageOptions = [5, 8, 10, 15, 20];

  const {
    data,
    isLoading,
    refetch: bookGroupCategoryRefetch,
  } = useQuery(
    KEY_BOOK_GROUP_CATEGORY_LIST,
    () => {
      const newData = {
        page: 0,
        rowsPerPage: 0,
        searchKey: 'book-group-category',
        searchWord: encodeSearchWord(searchKey, searchWord),
      };
      if (reqRequest) return getBookGroupCategoryList(newData);
      return null;
    },
    {
      onSuccess: res => {
        if (res) {
          setBookGroupCategoryList(res.bookGroupCategoryList);
        }
        setLoading(false);
      },
      onError: e => {
        console.log(e);
        setLoading(false);
      },
    }
  );

  const { refetch: bookGroupCategoryImageRefetch } = useQuery(
    KEY_BOOK_GROUP_CATEGORY_IMAGE_LIST,
    () => {
      const newData = {
        categorySeq: Number(selectedCategorySeq),
        page: page,
        rowsPerPage: rowsPerPage,
        searchKey: 'book-group-category-image',
        searchWord: encodeSearchWord(searchKey, searchWord),
      };
      if (reqRequest) return getBookGroupCategoryImageList(newData);
      return null;
    },
    {
      onSuccess: res => {
        if (res) {
          setBookGroupCategoryImageList(res.bookGroupCategoryImageList);
          setTotalRecords(res.totalRecordCount);
        }
        setLoading(false);
      },
      onError: e => {
        console.log(e);
        setLoading(false);
      },
    }
  );

  const prefetch = (newPage: number, newSeq: string) => {
    queryClient.prefetchQuery(KEY_BOOK_GROUP_CATEGORY_IMAGE_LIST, () => {
      const newData = {
        categorySeq: Number(newSeq),
        page: newPage,
        rowsPerPage: rowsPerPage,
        searchKey: 'book-group-category-image',
        searchWord: encodeSearchWord(searchKey, searchWord),
      };
      return getBookGroupCategoryImageList(newData);
    });
  };

  const handleButtonGroupChange = (event: any, newSeq: string) => {
    if (newSeq !== null) {
      setBookGroupCategoryImageList([]);
      setSelectedCategorySeq(newSeq);
      prefetch(1, newSeq);
    }
  };

  const handleChangePage = (_event: any, newPage: number) => {
    setPage(newPage);
    prefetch(newPage, selectedCategorySeq);
  };

  const handleClickImage = (idx: string) => {
    setImageUrl(bookGroupCategoryImageList[Number(idx)].imageUrl);
    onClose();
  };

  useEffect(() => {
    if (reqRequest) {
      setLoading(true);
      bookGroupCategoryRefetch();
      bookGroupCategoryImageRefetch();
    }
  }, [reqRequest]);

  useEffect(() => {
    if (open) {
      setReqRequest(true);
    } else {
      setSelectedCategorySeq('0');
      setBookGroupCategoryList([]);
      setReqRequest(false);
    }
  }, [open]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            minHeight: '90vh',
            minWidth: '60vw',
          },
        }}
      >
        <DialogTitle>
          <Box className='flex_between'>
            <Box>
              <Typography>썸네일 이미지 선택</Typography>
            </Box>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider sx={{ m: 0, p: 0 }} />
        <DialogContent>
          <Box>
            <ToggleButtonGroup
              color='primary'
              value={selectedCategorySeq}
              exclusive
              onChange={handleButtonGroupChange}
            >
              <ToggleButton
                value='0'
                sx={{ width: tabWidth, color: '#20C997' }}
              >
                전체
              </ToggleButton>
              {bookGroupCategoryList.map((item: any, idx: number) => (
                <ToggleButton
                  value={`${item.bookGroupCategorySeq}`}
                  sx={{ width: tabWidth, color: '#20C997' }}
                >
                  {item.categoryName}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          <Box
            sx={{
              widht: '100%',
              height: '60vh',
              overflowY: 'scroll',
              mt: '1rem',
              mb: '1rem',
            }}
          >
            {bookGroupCategoryImageList &&
            bookGroupCategoryImageList.length > 0 ? (
              <Grid
                container
                spacing={{ xs: 2, md: 3 }}
                columns={{ xs: 4, sm: 8, md: 12 }}
              >
                {bookGroupCategoryImageList.map((image: any, index: number) => (
                  <Grid item xs={2} sm={4} md={4} key={`image_key_${index}`}>
                    <Button onClick={() => handleClickImage(`${index}`)}>
                      <img
                        src={`${loginVal.value.user.cloudFront}${image.imageUrl}`}
                        alt={`alt-${index}`}
                        style={{ width: '100%' }}
                      />
                    </Button>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box>
                <Box>데이터가 없습니다.</Box>
              </Box>
            )}
          </Box>
          {bookGroupCategoryImageList &&
            bookGroupCategoryImageList.length > 0 && (
              <SwPagination
                page={page}
                handleChangePage={handleChangePage}
                count={
                  bookGroupCategoryImageList &&
                  Math.ceil(totalRecords / rowsPerPage)
                }
              />
            )}
        </DialogContent>
        <Divider sx={{ m: 0, p: 0 }} />
        <DialogActions>
          <Button variant='contained' color='info' onClick={onClose}>
            등록
          </Button>
        </DialogActions>
      </Dialog>
      {loading && <SwLoading />}
    </>
  );
};
